import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ContactEditor } from "@/components/ContactEditor";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Plus, KeyRound, User, MapPin, CheckCircle2, PauseCircle, Building2 } from "lucide-react";
import { LoadError, errMessage } from "@/components/LoadError";
import { toast } from "sonner";
import { TijdelijkWachtwoordDialog } from "@/components/TijdelijkWachtwoord";

const input = "w-full px-3 py-2 rounded-md border border-zinc-300 focus:outline-none focus:ring-2 focus:ring-blue-600 text-sm text-zinc-900";
const btnPrimary = "flex items-center gap-1.5 px-4 py-2 rounded-md bg-zinc-950 text-white text-sm font-semibold uppercase tracking-wider hover:bg-zinc-800 transition-colors disabled:opacity-60";
const btnGhost = "flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-zinc-300 text-sm font-medium text-zinc-700 hover:border-zinc-400 transition-colors";
const card = "bg-white rounded-[10px] border border-zinc-200 p-4 md:p-5";

function err(e, fallback) { toast.error(e?.response?.data?.detail || fallback); }

export default function LocatieAccounts() {
  const [meta, setMeta] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [metaError, setMetaError] = useState("");
  const [usersError, setUsersError] = useState("");
  const [nu, setNu] = useState({ username: "", locatie: "" });
  const [confirm, setConfirm] = useState(null); // {title, desc, confirmLabel, danger, onConfirm}
  const [tijdelijk, setTijdelijk] = useState(null);

  const loadMeta = () => api.get("/meta").then((r) => { setMeta(r.data); setMetaError(""); }).catch((e) => setMetaError(errMessage(e)));
  const loadUsers = () => api.get("/users").then((r) => { setUsers(r.data); setUsersLoaded(true); setUsersError(""); }).catch((e) => { setUsersLoaded(false); setUsersError(errMessage(e)); });
  useEffect(() => { loadMeta(); loadUsers(); }, []);

  if (!meta) return (
    <div data-testid="locatie-accounts-page" className="space-y-3">
      {metaError ? <LoadError error={metaError} onRetry={loadMeta} testid="locatie-accounts-load-error" /> : <div className="text-sm text-zinc-500">Laden…</div>}
    </div>
  );

  // Mislukte /users-load: TOON ALLEEN de foutbanner. Geen lijst, geen lege staat, geen aanmaakformulier —
  // de lege staat verschijnt uitsluitend na een GESLAAGDE load, en freeLocs zou zonder users elke locatie
  // (ook bezette) als 'vrij' aanbieden.
  if (!usersLoaded) return (
    <div data-testid="locatie-accounts-page">
      <div className="mb-3 md:mb-4">
        <h1 className="font-heading text-2xl font-black tracking-tight text-zinc-900">Locatie-accounts</h1>
        <p className="text-sm text-zinc-500">Beheer de bedrijfsleider-accounts per locatie: aanmaken, wachtwoord resetten en (in)activeren. Meer instellingen lopen via oversight.</p>
      </div>
      {usersError
        ? <LoadError error={usersError} onRetry={loadUsers} testid="locatie-accounts-users-error" />
        : <div className="text-sm text-zinc-500">Laden…</div>}
    </div>
  );

  const buckets = meta.generalBuckets || [];
  // De server geeft een OM UITSLUITEND bedrijfsleider-accounts terug — we vertrouwen dat en filteren niet nog eens.
  const bls = [...users].sort((a, b) => (a.locatie || "").localeCompare(b.locatie || ""));
  const activeBl = bls.filter((u) => u.active !== false);
  const takenLocs = new Set(activeBl.filter((u) => u.locatie).map((u) => u.locatie));
  const allLocs = (meta.locations || []).filter((l) => !buckets.includes(l));
  const freeLocs = allLocs.filter((l) => !takenLocs.has(l));

  const create = async () => {
    const uname = nu.username.trim();
    if (!uname) { toast.error("Vul een gebruikersnaam in"); return; }
    if (!nu.locatie) { toast.error("Kies een locatie voor de bedrijfsleider"); return; }
    try {
      const r = await api.post("/users", { username: uname, role: "bedrijfsleider", locatie: nu.locatie });
      if (r.data.tempPassword) setTijdelijk({ username: uname, wachtwoord: r.data.tempPassword });
      else toast.success("Account aangemaakt");
      setNu({ username: "", locatie: "" });
      loadUsers(); loadMeta();
    } catch (e) { err(e, "Aanmaken mislukt"); }
  };

  const resetPwd = async (username) => {
    try {
      const r = await api.post(`/users/${username}/reset-password`);
      setTijdelijk({ username, wachtwoord: r.data.tempPassword });
      loadUsers();
    } catch (e) { err(e, "Reset mislukt"); }
  };

  const setActive = async (username, active) => {
    try {
      await api.patch(`/users/${username}/active`, { active });
      toast.success(active ? "Account geheractiveerd" : "Account gedeactiveerd");
      loadUsers();
    } catch (e) { err(e, "Bijwerken mislukt"); }
  };

  return (
    <div data-testid="locatie-accounts-page">
      <TijdelijkWachtwoordDialog info={tijdelijk} onClose={() => setTijdelijk(null)} />

      <div className="mb-3 md:mb-4">
        <h1 className="font-heading text-2xl font-black tracking-tight text-zinc-900">Locatie-accounts</h1>
        <p className="text-sm text-zinc-500">Beheer de bedrijfsleider-accounts per locatie: aanmaken, wachtwoord resetten en (in)activeren. Meer instellingen lopen via oversight.</p>
      </div>

      {/* Werklijst: locaties zonder bedrijfsleider */}
      <div className={`${card} mb-3 md:mb-4`} data-testid="locaties-zonder-bl">
        <div className="flex items-center gap-2 mb-1">
          <MapPin className="h-5 w-5 text-amber-600" />
          <h2 className="font-heading text-lg font-bold text-zinc-900">Locaties zonder bedrijfsleider</h2>
        </div>
        <p className="text-sm text-zinc-500 mb-3">Dit is je werklijst — maak hier een account aan zodat de locatie zelf kan aanleveren.</p>
        {freeLocs.length === 0 ? (
          <p className="text-sm text-emerald-700 font-medium" data-testid="geen-lege-locaties">Elke locatie heeft een actieve bedrijfsleider.</p>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid="lege-locaties-lijst">
            {freeLocs.map((l) => (
              <button key={l} type="button" data-testid={`lege-locatie-${l}`}
                onClick={() => { setNu({ username: "", locatie: l }); }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 transition-colors">
                <MapPin className="h-3.5 w-3.5" /> {l}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Account aanmaken */}
      <div className={`${card} mb-3 md:mb-4`} data-testid="nieuw-account-card">
        <div className="flex items-center gap-2 mb-1">
          <Plus className="h-5 w-5 text-zinc-700" />
          <h2 className="font-heading text-lg font-bold text-zinc-900">Nieuw bedrijfsleider-account</h2>
        </div>
        <p className="text-sm text-zinc-500 mb-3">Kies een gebruikersnaam en een locatie. De bedrijfsleider krijgt een tijdelijk wachtwoord en kiest bij de eerste login zelf een nieuw wachtwoord.</p>
        <div className="flex flex-wrap items-center gap-2">
          <input className={`${input} max-w-[200px]`} data-testid="new-bl-username" placeholder="Gebruikersnaam"
            value={nu.username} onChange={(e) => setNu({ ...nu, username: e.target.value })} />
          <select className={`${input} max-w-[240px]`} data-testid="new-bl-locatie"
            value={nu.locatie} onChange={(e) => setNu({ ...nu, locatie: e.target.value })}>
            <option value="">Kies locatie…</option>
            {allLocs.map((l) => (
              <option key={l} value={l} disabled={takenLocs.has(l)}>{l}{takenLocs.has(l) ? " — al bezet" : ""}</option>
            ))}
          </select>
          <button className={btnPrimary} data-testid="create-bl-btn" onClick={create}><Plus className="h-4 w-4" /> Aanmaken</button>
        </div>
      </div>

      {/* Overzicht bedrijfsleiders */}
      <div className={card} data-testid="bl-overzicht-card">
        <div className="flex items-center gap-2 mb-1">
          <Building2 className="h-5 w-5 text-zinc-700" />
          <h2 className="font-heading text-lg font-bold text-zinc-900">Bedrijfsleiders</h2>
        </div>
        <p className="text-sm text-zinc-500 mb-4">Alle bedrijfsleider-accounts met hun locatie en status. Deactiveren behoudt alle historie.</p>
        {bls.length === 0 ? (
          <div className="text-center py-10 text-sm text-zinc-400" data-testid="geen-bl">Nog geen bedrijfsleider-accounts.</div>
        ) : (
          <div className="space-y-2">
            {bls.map((u) => {
              const inactive = u.active === false;
              return (
                <div key={u.username} data-testid={`bl-row-${u.username}`}
                  className={`flex flex-wrap items-center gap-2 p-3 rounded-md border ${inactive ? "border-zinc-200 bg-zinc-100/70 opacity-80" : "border-zinc-200"}`}>
                  <div className="flex items-center gap-2 min-w-[150px] flex-1">
                    <User className="h-4 w-4 text-zinc-400" />
                    <span className="font-semibold text-zinc-900 capitalize">{u.username}</span>
                    {inactive
                      ? <span data-testid={`bl-inactief-${u.username}`} className="flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-zinc-300 text-zinc-700 font-semibold"><PauseCircle className="h-3 w-3" /> inactief</span>
                      : <span data-testid={`bl-actief-${u.username}`} className="flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold"><CheckCircle2 className="h-3 w-3" /> actief</span>}
                  </div>
                  <div className="flex items-center gap-1.5 text-sm text-zinc-500 min-w-[140px]">
                    <MapPin className="h-4 w-4 text-zinc-400" />
                    <span data-testid={`bl-locatie-${u.username}`}>{u.locatie || <span className="italic text-zinc-400">geen locatie</span>}</span>
                  </div>
                  <button className={btnGhost} data-testid={`reset-bl-${u.username}`}
                    onClick={() => setConfirm({
                      title: `Wachtwoord van "${u.username}" resetten?`,
                      desc: "Het account krijgt een nieuw tijdelijk wachtwoord, dat je daarna te zien krijgt om door te geven. De bedrijfsleider moet bij de eerstvolgende login verplicht zelf een nieuw wachtwoord kiezen.",
                      confirmLabel: "Resetten",
                      onConfirm: () => resetPwd(u.username),
                    })}>
                    <KeyRound className="h-4 w-4" /> Reset wachtwoord
                  </button>
                  <button data-testid={`toggle-bl-${u.username}`}
                    onClick={() => {
                      if (inactive) { setActive(u.username, true); return; }
                      setConfirm({
                        title: `Account "${u.username}" deactiveren?`,
                        desc: "De bedrijfsleider kan niet meer inloggen. Alle historie blijft behouden en je kunt later heractiveren.",
                        confirmLabel: "Deactiveren", danger: true,
                        onConfirm: () => setActive(u.username, false),
                      });
                    }}
                    className={`px-3 py-2 rounded-md border text-sm font-medium transition-colors ${inactive ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50" : "border-amber-300 text-amber-700 hover:bg-amber-50"}`}>
                    {inactive ? "Heractiveren" : "Deactiveren"}
                  </button>
                  <ContactEditor user={u} onSaved={loadUsers} />
                </div>
              );
            })}
          </div>
        )}
      </div>

      <AlertDialog open={!!confirm} onOpenChange={(o) => { if (!o) setConfirm(null); }}>
        <AlertDialogContent data-testid="la-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm?.title}</AlertDialogTitle>
            <AlertDialogDescription>{confirm?.desc}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="la-confirm-cancel">Annuleren</AlertDialogCancel>
            <AlertDialogAction data-testid="la-confirm-ok" className={confirm?.danger ? "bg-amber-600 hover:bg-amber-700" : ""}
              onClick={() => { confirm?.onConfirm?.(); setConfirm(null); }}>{confirm?.confirmLabel || "Bevestigen"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
